import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { CandleChart } from "@/components/CandleChart";
import { DisclaimerBanner } from "@/components/DisclaimerBanner";
import { Radio, TrendingUp, TrendingDown } from "lucide-react";

const TIMEFRAMES = ["1m", "5m", "15m", "1h", "4h", "1d", "1w"];
const MARKETS = ["crypto", "stocks", "forex", "indices", "commodities"];

export default function Charts() {
  const [symbols, setSymbols] = useState({});
  const [market, setMarket] = useState("crypto");
  const [symbol, setSymbol] = useState("BTC");
  const [tf, setTf] = useState("1h");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sma, setSma] = useState(false);
  const [ema, setEma] = useState(false);

  useEffect(() => {
    api.get("/market/symbols").then(({ data }) => setSymbols(data)).catch(() => {});
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/market/candles", { params: { symbol, timeframe: tf, market } });
      setData(data);
    } catch { setData(null); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [symbol, tf, market]);

  const onMarket = (m) => {
    setMarket(m);
    const first = symbols[m]?.[0]?.symbol;
    if (first) setSymbol(first);
  };

  const candles = data?.candles || [];
  const last = candles[candles.length - 1];
  const first = candles[0];
  const change = last && first ? ((last.c - first.o) / first.o) * 100 : 0;
  const overlays = useMemo(() => ({ sma: sma ? 20 : 0, ema: ema ? 50 : 0 }), [sma, ema]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight">Interactive Charts</h1>
        <p className="text-slate-400 mt-1">Switch timeframes, markets and indicators to see how price action changes.</p>
      </div>

      {/* Market tabs */}
      <div className="flex flex-wrap gap-2">
        {MARKETS.map((m) => (
          <button key={m} data-testid={`market-${m}`} onClick={() => onMarket(m)}
            className={`px-4 py-2 rounded-lg text-sm font-medium capitalize transition-colors ${market === m ? "bg-blue-600 text-white" : "bg-slate-800/60 text-slate-400 hover:text-slate-100"}`}>
            {m}
          </button>
        ))}
      </div>

      <div className="bg-[#0F172A] border border-slate-800 rounded-2xl overflow-hidden">
        <div className="flex flex-wrap items-center gap-3 p-4 border-b border-slate-800">
          <select data-testid="symbol-select" value={symbol} onChange={(e) => setSymbol(e.target.value)}
            className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm font-mono focus:border-blue-500 focus:outline-none">
            {(symbols[market] || []).map((s) => <option key={s.symbol} value={s.symbol}>{s.symbol} — {s.name}</option>)}
          </select>

          {last && (
            <div className="flex items-center gap-3">
              <span className="font-mono text-lg font-semibold text-white">{last.c}</span>
              <span className={`flex items-center gap-1 text-sm font-mono font-semibold ${change >= 0 ? "text-bull" : "text-bear"}`}>
                {change >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                {change >= 0 ? "+" : ""}{change.toFixed(2)}%
              </span>
            </div>
          )}

          <div className="ml-auto flex items-center gap-2">
            <button data-testid="toggle-sma" onClick={() => setSma((v) => !v)}
              className={`px-3 py-1.5 rounded-lg text-xs font-mono font-semibold border transition-colors ${sma ? "bg-amber-500/15 border-amber-500/40 text-amber-300" : "border-slate-700 text-slate-400 hover:text-slate-200"}`}>SMA 20</button>
            <button data-testid="toggle-ema" onClick={() => setEma((v) => !v)}
              className={`px-3 py-1.5 rounded-lg text-xs font-mono font-semibold border transition-colors ${ema ? "bg-violet-500/15 border-violet-500/40 text-violet-300" : "border-slate-700 text-slate-400 hover:text-slate-200"}`}>EMA 50</button>
          </div>
        </div>

        <div className="px-2 py-3">
          {loading ? (
            <div className="h-[420px] grid place-items-center text-slate-500">Loading chart…</div>
          ) : (
            <CandleChart candles={candles} overlays={overlays} />
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 p-4 border-t border-slate-800">
          {TIMEFRAMES.map((t) => (
            <button key={t} data-testid={`tf-${t}`} onClick={() => setTf(t)}
              className={`px-3.5 py-1.5 rounded-lg text-sm font-mono font-semibold transition-colors ${tf === t ? "bg-blue-600 text-white" : "bg-slate-800/60 text-slate-400 hover:text-slate-100"}`}>
              {t}
            </button>
          ))}
          {data && (
            <span data-testid="data-source" className="ml-auto flex items-center gap-1.5 text-xs font-mono text-slate-500">
              <Radio className="w-3.5 h-3.5 text-emerald-400 live-dot" />
              {data.source === "coingecko" ? "Live data · CoinGecko" : "Simulated demo data"}
            </span>
          )}
        </div>
      </div>

      <DisclaimerBanner compact />
    </div>
  );
}
